import type { CampaignAnalysisResult } from '@/entities/campaign/generated/campaign-type';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/shadcn/card';
import { formatDateTime } from '@/shared/utils/date/date-formats';
import { FileDown, LoaderCircle } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';

import { reportSectionMap } from '../../helpers/analysis-report';
import { exportCampaignAnalysisReportPdf } from '../pdf-view/campaign-analysis-report-pdf-export';
import { ReportAppendixSection, ReportBackgroundList } from './appendix-section';
import { ContentGuideSection } from './content-guide-section';
import { CreatorGuideSection } from './creator-guide-section';
import { ExecutiveSummarySection } from './executive-summary-section';
import { InsightsSection } from './insights-section';
import { OverviewSection } from './overview-section';
import { ReportSection } from './report-section';
import { ReportSectionNav } from './report-section-nav';
import { TopVideosSection } from './top-videos-section';

export function CampaignAnalysisReport({
  actions,
  onOpenDiscovery,
  report,
}: {
  actions?: ReactNode;
  onOpenDiscovery: (prompt: string) => void;
  report: CampaignAnalysisResult;
}) {
  const isExportingRef = useRef(false);
  const [isExporting, setIsExporting] = useState(false);

  async function handleExportPdf() {
    if (isExportingRef.current) return;

    isExportingRef.current = true;
    setIsExporting(true);

    try {
      await exportCampaignAnalysisReportPdf({
        reportElement: <CampaignAnalysisReportDocument mode="print" report={report} />,
        reportTitle: `${report.meta.dataset.campaign_name} - ${report.meta.title}`,
      });
    } finally {
      isExportingRef.current = false;
      setIsExporting(false);
    }
  }

  const saveAsPdfAction = (
    <Button
      aria-busy={isExporting}
      disabled={isExporting}
      onClick={() => void handleExportPdf()}
      size="sm"
      variant="ghost"
    >
      {isExporting ? (
        <LoaderCircle className="size-4 animate-spin" />
      ) : (
        <FileDown className="size-4" />
      )}
      {isExporting ? 'Preparing PDF…' : 'Save as PDF'}
    </Button>
  );

  return (
    <CampaignAnalysisReportDocument
      actions={actions}
      mode="screen"
      onOpenDiscovery={onOpenDiscovery}
      report={report}
      saveAsPdfAction={saveAsPdfAction}
    />
  );
}

export function CampaignAnalysisReportDocument({
  actions,
  mode,
  onOpenDiscovery = () => undefined,
  report,
  saveAsPdfAction,
}: {
  actions?: ReactNode;
  mode: 'print' | 'screen';
  onOpenDiscovery?: (prompt: string) => void;
  report: CampaignAnalysisResult;
  saveAsPdfAction?: ReactNode;
}) {
  const [isSectionNavStuck, setIsSectionNavStuck] = useState(false);

  return (
    <Card
      data-campaign-analysis-print-root
      data-campaign-analysis-report-mode={mode}
      className="border-border bg-card min-w-0 gap-0 overflow-visible rounded-2xl shadow-sm"
    >
      <CardHeader data-campaign-analysis-report-header className="px-8 pt-8 pb-20">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-col lg:max-w-162.5">
            <div data-campaign-analysis-report-title-block className="mb-20 flex flex-col gap-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className="border-foreground bg-background text-foreground rounded-full px-2.5 py-1 text-xs font-semibold"
                >
                  Campaign analysis report
                </Badge>
                <span className="text-muted-foreground text-xs font-medium">
                  {formatDateTime(report.meta.analysis_started_at)}
                </span>
              </div>
              <CardTitle
                data-campaign-analysis-report-title
                className="text-foreground max-w-3xl text-3xl leading-10 font-bold tracking-tight"
              >
                {report.meta.title}
              </CardTitle>
            </div>
            <ReportBackgroundList meta={report.meta} appendix={report.appendix} />
          </div>
          {mode === 'screen' && (saveAsPdfAction || actions) ? (
            <div data-campaign-analysis-report-actions className="flex shrink-0 items-center gap-1">
              {isSectionNavStuck ? null : saveAsPdfAction}
              {actions}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {mode === 'screen' ? (
          <ReportSectionNav
            onStickyChange={setIsSectionNavStuck}
            stickyAction={isSectionNavStuck ? saveAsPdfAction : null}
          />
        ) : null}

        <ExecutiveSummarySection report={report} />

        <ReportSection id={reportSectionMap.overview.id} title={reportSectionMap.overview.label}>
          <OverviewSection mode={mode} overview={report.overview} />
        </ReportSection>

        <ReportSection
          id={reportSectionMap.topVideos.id}
          title={reportSectionMap.topVideos.label}
          description="Top-performing posts by the selected performance score."
        >
          <TopVideosSection videos={report.top_post_examples} />
        </ReportSection>

        <ReportSection
          title={reportSectionMap.insights.label}
          description="Evidence-backed performance patterns from campaign post groups."
          id={reportSectionMap.insights.id}
        >
          <InsightsSection
            insightCards={report.executive_summary.key_insight_cards ?? []}
            insights={report.insights}
          />
        </ReportSection>

        <ReportSection
          id={reportSectionMap.contentGuide.id}
          title={reportSectionMap.contentGuide.label}
          description="Turn the strongest insights into concrete creator brief instructions."
        >
          <ContentGuideSection content_creation_guides={report.content_creation_guides} />
        </ReportSection>

        <ReportSection
          id={reportSectionMap.creatorGuide.id}
          title={reportSectionMap.creatorGuide.label}
          description="Use these recommended creator prompts in Discovery to source the next creator set."
        >
          <CreatorGuideSection
            creator_discovery_guides={report.creator_discovery_guides}
            onOpenDiscovery={onOpenDiscovery}
          />
        </ReportSection>

        <ReportSection
          id={reportSectionMap.appendix.id}
          title={reportSectionMap.appendix.label}
          description="Reference details used to generate this report."
        >
          <ReportAppendixSection appendix={report.appendix} />
        </ReportSection>
      </CardContent>
    </Card>
  );
}
